import React from 'react';

const choose = (condition, whenTrue, whenFalse) => (condition ? whenTrue() : whenFalse());

const firstTruthy = (...readers) => readers.reduce((result, read) => result || read(), undefined);
const everyTruthy = (...readers) => readers.reduce((result, read) => result && read(), true);

function readText(value, fallback = '—') {
  const text = String(value || '').trim();
  return text || fallback;
}

function resolveSelectedInstance(instances = [], selectedInstanceId = '') {
  const selectedId = String(selectedInstanceId || '').trim();
  return instances.find((instance) => instance.instanceId === selectedId) || instances[0] || null;
}

function switchDevQueryValue(value, switchParam) {
  if (typeof window === 'undefined' || !value) {
    return;
  }
  const nextUrl = new URL(window.location.href);
  nextUrl.searchParams.set(switchParam, value);
  window.location.assign(nextUrl.toString());
}

export default function DevTestToolbar({ config = {}, viewModel = {} }) {
  const fixtures = React.useMemo(() => (choose(Array.isArray(config.fixtures), () => (config.fixtures), () => ([]))), [config.fixtures]);
  const [selectedFixtureId, setSelectedFixtureId] = React.useState(firstTruthy(() => (Object(fixtures[0]).fixtureId), () => ('')));
  const [fixtureBusy, setFixtureBusy] = React.useState(false);
  const [fixtureError, setFixtureError] = React.useState('');

  React.useEffect(() => {
    if (firstTruthy(() => (selectedFixtureId), () => (!Object(fixtures[0]).fixtureId))) {
      return;
    }
    setSelectedFixtureId(fixtures[0].fixtureId);
  }, [fixtures, selectedFixtureId]);

  const handleSeedFixture = async () => {
    const fixtureId = String(firstTruthy(() => (selectedFixtureId), () => (''))).trim();
    const seedFixture = Object(config.devActions).seedFixture;
    if (firstTruthy(() => (!fixtureId), () => (typeof seedFixture !== 'function'))) {
      return;
    }
    setFixtureBusy(true);
    setFixtureError('');
    try {
      const result = await seedFixture(fixtureId);
      const project = Object(result).project;
      const selectedSessionId = String(firstTruthy(() => (Object(result).selectedSessionId), () => (''))).trim();
      viewModel.sessionList?.onProjectSelect?.(project);
      if (selectedSessionId) {
        await viewModel.sessionList?.onSelectSession?.(selectedSessionId);
      }
    } catch (error) {
      setFixtureError(firstTruthy(() => (Object(error).message), () => ('Fixture seed failed')));
    } finally {
      setFixtureBusy(false);
    }
  };

  if (!config.enabled) {
    return null;
  }

  const instances = choose(Array.isArray(config.instances), () => (config.instances), () => ([]));
  const selectedInstanceId = readText(firstTruthy(() => (config.selectedInstanceId), () => (config.instanceId)), '');
  const selectedInstance = resolveSelectedInstance(instances, selectedInstanceId);
  const selectedValue = firstTruthy(() => (Object(selectedInstance).instanceId), () => (selectedInstanceId));
  const domain = readText(firstTruthy(() => (Object(selectedInstance).domainPackDomain), () => (config.domainPackDomain)));
  const version = readText(firstTruthy(() => (Object(selectedInstance).domainPackVersion), () => (config.domainPackVersion)));

  return (
    <div
      data-testid="flare-dev-test-toolbar"
      style={{
        alignItems: 'center',
        backdropFilter: 'blur(14px)',
        background: 'rgba(8, 12, 22, 0.62)',
        borderBottom: '1px solid rgba(125, 211, 252, 0.22)',
        boxShadow: 'inset 0 -1px 0 rgba(14, 165, 233, 0.10)',
        boxSizing: 'border-box',
        color: 'rgba(226, 232, 240, 0.92)',
        display: 'flex',
        flex: '0 0 auto',
        fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
        fontSize: 11,
        fontWeight: 400,
        gap: 10,
        height: 30,
        minHeight: 30,
        overflow: 'hidden',
        padding: '0 10px',
        whiteSpace: 'nowrap',
        width: '100%',
        zIndex: 10,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          background: 'rgba(34, 211, 238, 0.78)',
          boxShadow: '0 0 10px rgba(34, 211, 238, 0.32)',
          flex: '0 0 auto',
          height: 14,
          width: 2,
        }}
      />
      <span
        style={{
          color: 'rgba(125, 211, 252, 0.92)',
          flex: '0 0 auto',
          fontSize: 10,
          fontWeight: 500,
          letterSpacing: 1.4,
          textTransform: 'uppercase',
        }}
      >
        FLARE DEV
      </span>
      <label
        style={{
          alignItems: 'center',
          display: 'inline-flex',
          flex: '0 0 auto',
          gap: 6,
          minWidth: 0,
        }}
      >
        <span style={{ color: 'rgba(148, 163, 184, 0.86)', fontSize: 10 }}>Pack</span>
        <select
          aria-label="Dev pack"
          onChange={(event) => switchDevQueryValue(
            event.target.value,
            firstTruthy(() => (config.switchParam), () => ('flare_instance')),
          )}
          style={{
            appearance: 'none',
            background: 'rgba(15, 23, 42, 0.72)',
            border: '1px solid rgba(125, 211, 252, 0.24)',
            borderRadius: 2,
            color: 'rgba(241, 245, 249, 0.95)',
            fontSize: 11,
            fontWeight: 400,
            height: 22,
            lineHeight: '20px',
            maxWidth: 180,
            outline: 'none',
            padding: '0 20px 0 7px',
          }}
          value={selectedValue}
        >
          {choose(instances.length, () => (instances.map((instance) => (
            <option key={instance.instanceId} value={instance.instanceId}>
              {firstTruthy(() => (instance.label), () => (instance.instanceId))}
            </option>
          ))), () => (<option value={selectedValue}>{firstTruthy(() => (selectedValue), () => ('default'))}</option>))}
        </select>
      </label>
      <span
        style={{
          color: 'rgba(148, 163, 184, 0.82)',
          flex: '1 1 auto',
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {readText(selectedValue)} / {domain} / {version}
      </span>
      {choose(everyTruthy(() => (fixtures.length > 0), () => (typeof Object(config.devActions).seedFixture === 'function')), () => (<label
          style={{
            alignItems: 'center',
            display: 'inline-flex',
            flex: '0 0 auto',
            gap: 6,
            minWidth: 0,
          }}
        >
          <span style={{ color: 'rgba(148, 163, 184, 0.86)', fontSize: 10 }}>Fixture</span>
          <select
            aria-label="Dev fixture"
            onChange={(event) => setSelectedFixtureId(event.target.value)}
            style={{
              appearance: 'none',
              background: 'rgba(15, 23, 42, 0.72)',
              border: '1px solid rgba(125, 211, 252, 0.24)',
              borderRadius: 2,
              color: 'rgba(241, 245, 249, 0.95)',
              fontSize: 11,
              fontWeight: 400,
              height: 22,
              lineHeight: '20px',
              maxWidth: 180,
              outline: 'none',
              padding: '0 20px 0 7px',
            }}
            value={selectedFixtureId}
          >
            {fixtures.map((fixture) => (
              <option key={fixture.fixtureId} value={fixture.fixtureId}>
                {firstTruthy(() => (fixture.label), () => (fixture.fixtureId))}
              </option>
            ))}
          </select>
          <button
            aria-label="加载测试数据"
            disabled={fixtureBusy}
            onClick={handleSeedFixture}
            style={{
              background: 'rgba(14, 165, 233, 0.20)',
              border: '1px solid rgba(125, 211, 252, 0.24)',
              borderRadius: 2,
              color: 'rgba(241, 245, 249, 0.95)',
              cursor: fixtureBusy ? 'wait' : 'pointer',
              fontSize: 11,
              height: 22,
              padding: '0 8px',
            }}
            type="button"
          >
            {fixtureBusy ? '加载中' : '加载'}
          </button>
        </label>), () => (null))}
      {choose(fixtureError, () => (<span style={{ color: '#fca5a5', flex: '0 0 auto' }}>{fixtureError}</span>), () => (null))}
    </div>
  );
}
